'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';

export type GroupByOption<TField extends string> = {
  value: TField;
  label: string;
};

export type GroupByFieldSelection<TField extends string> = {
  options: ReadonlyArray<GroupByOption<TField>>;
  groupByField: TField | null;
  onGroupByFieldChange: (field: TField | null) => void;
};

export function useGroupByFieldMenuItems<TField extends string>({
  options,
  groupByField,
  onGroupByFieldChange,
}: GroupByFieldSelection<TField>) {
  const t = useTranslations('designSystem.groupBy');
  const selectedFieldLabel =
    options.find((option) => option.value === groupByField)?.label ?? t('none');
  const fieldMenuItems = useMemo(
    () => [
      {
        value: 'none',
        label: t('none'),
        selected: groupByField == null,
        onClick: () => onGroupByFieldChange(null),
      },
      ...options.map((option) => ({
        value: option.value,
        label: option.label,
        selected: groupByField === option.value,
        onClick: () => onGroupByFieldChange(option.value),
      })),
    ],
    [groupByField, onGroupByFieldChange, options, t],
  );

  return { fieldMenuItems, selectedFieldLabel };
}
